import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import {
  ShieldCheck, Building2, Users, Briefcase, FileText, LibraryBig,
  Inbox, Mail, Activity, LogOut, ArrowLeft, LifeBuoy, Menu, X,
} from "lucide-react";

/**
 * Internal TradeDocs Admin Portal (§33 of Phase 2 spec).
 *
 * Access is granted only to users whose backend `role == "admin"`. The set of
 * admin emails is controlled server-side via the ADMIN_EMAILS environment
 * variable — see backend/server.py:register/login.
 *
 * The current skeleton renders the sidebar sections listed in the spec and a
 * high-level metrics dashboard (via /api/admin/summary). Individual section
 * pages are stubs at this stage — content is planned for Release 2.2/2.3.
 */
export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event) => event.key === "Escape" && setMobileNavOpen(false);
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [mobileNavOpen]);

  if (!user) return <Navigate to="/signin" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--td-bg)" }}>
        <div className="card-surface p-10 max-w-md text-center">
          <ShieldCheck size={28} className="text-[var(--td-gold)] mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-3">Admin access only</h1>
          <p className="text-[var(--td-text-2)] text-sm mb-6 leading-relaxed">
            The internal TradeDocs admin portal is limited to authorised operators.
            Contact your workspace administrator if you believe you should have access.
          </p>
          <button
            onClick={() => nav("/app/dashboard")}
            className="btn-gold inline-flex items-center gap-2"
          >
            <ArrowLeft size={13} /> Back to workspace
          </button>
        </div>
      </div>
    );
  }

  const items = [
    { to: "/admin",             end: true,  label: "Overview",        Icon: Activity,    testid: "admin-nav-overview" },
    { to: "/admin/orgs",        label: "Client Organisations",         Icon: Building2,   testid: TESTIDS.adminNavOrgs },
    { to: "/admin/users",       label: "Users",                        Icon: Users,       testid: TESTIDS.adminNavUsers },
    { to: "/admin/deals",       label: "Deals",                        Icon: Briefcase,   testid: TESTIDS.adminNavDeals },
    { to: "/admin/documents",   label: "Documents",                    Icon: FileText,    testid: "admin-nav-documents" },
    { to: "/admin/templates",   label: "Template Management",          Icon: LibraryBig,  testid: TESTIDS.adminNavTemplates },
    { to: "/admin/intakes",     label: "Intake Queue",                 Icon: Inbox,       testid: TESTIDS.adminNavIntake },
    { to: "/admin/early",       label: "Marketing & Demos",            Icon: Mail,        testid: TESTIDS.adminNavEarly },
    { to: "/admin/expert",      label: "Expert Support",               Icon: LifeBuoy,    testid: "admin-nav-expert" },
    { to: "/admin/audit",       label: "Audit Logs",                   Icon: ShieldCheck, testid: TESTIDS.adminNavAudit },
  ];

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };

  return (
    <div className="min-h-[100dvh] flex flex-col lg:flex-row" style={{ background: "var(--td-bg)" }}>
      <header className="lg:hidden sticky top-0 z-50 h-16 px-4 flex items-center justify-between border-b border-[var(--td-border)] bg-[var(--td-surface)]/95 backdrop-blur">
        <button type="button" onClick={() => setMobileNavOpen(true)} aria-label="Open admin navigation"
                aria-expanded={mobileNavOpen} aria-controls="mobile-admin-navigation"
                className="w-11 h-11 inline-flex items-center justify-center border border-[var(--td-border)]">
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 min-w-0"><ShieldCheck size={17} className="text-[var(--td-gold)] shrink-0" /><span className="font-serif truncate">TradeDocs Admin</span></div>
        <button onClick={() => nav("/app/dashboard")} aria-label="Back to workspace"
                className="w-11 h-11 inline-flex items-center justify-center border border-[var(--td-border)]">
          <ArrowLeft size={18} />
        </button>
      </header>

      {mobileNavOpen && <button type="button" aria-label="Close admin navigation" onClick={() => setMobileNavOpen(false)} className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm lg:hidden" />}

      <aside id="mobile-admin-navigation"
             className={`fixed inset-y-0 left-0 z-[70] w-[min(88vw,20rem)] h-[100dvh] shrink-0 flex flex-col hairline-bottom transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:w-64 lg:translate-x-0 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
             style={{ background: "var(--td-surface)", borderRight: "1px solid var(--td-border)" }}>
        <div className="p-6 hairline-bottom">
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-[var(--td-gold)]" />
            <div>
              <div className="font-serif text-base leading-tight">TradeDocs</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)]">Admin Portal</div>
            </div>
          </div>
          <button type="button" onClick={() => setMobileNavOpen(false)} aria-label="Close admin navigation"
                  className="lg:hidden absolute top-3 right-3 w-11 h-11 inline-flex items-center justify-center border border-[var(--td-border)]">
            <X size={19} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 space-y-0.5">
          {items.map(({ to, end, label, Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[var(--td-bg)] text-[var(--td-gold)] border-l-2 border-[var(--td-gold)]"
                    : "text-[var(--td-text-2)] hover:text-[var(--td-text)] border-l-2 border-transparent"
                }`
              }
            >
              <Icon size={15} className="shrink-0" />
              <span className="font-serif text-[13px]">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 hairline-top">
          <div data-recording-sensitive="name" className="text-xs text-[var(--td-text-2)] mb-1 truncate">{user.full_name}</div>
          <div data-recording-sensitive="email" className="font-mono text-[10px] text-[var(--td-text-3)] truncate mb-3">{user.email}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/app/dashboard")}
              className="flex items-center gap-1.5 font-mono text-[10px] px-2 py-1 border border-[var(--td-border)] hover:border-[var(--td-gold)] hover:text-[var(--td-gold)] transition-colors uppercase tracking-widest"
            >
              Workspace
            </button>
            <button
              onClick={doLogout}
              className="flex items-center gap-1.5 font-mono text-[10px] px-2 py-1 border border-[var(--td-border)] hover:border-red-500 hover:text-red-400 transition-colors uppercase tracking-widest"
            >
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminOverview() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.adminSummary()
      .then(setSummary)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, []);
  const tiles = summary ? [
    { k: "users",        label: "Users",             v: summary.users },
    { k: "deals",        label: "Deals",             v: summary.deals },
    { k: "drafts",       label: "Drafts",            v: summary.drafts },
    { k: "intakes",      label: "Intake requests",   v: summary.intakes },
    { k: "early_access", label: "Early-access",      v: summary.early_access },
    { k: "templates",    label: "Templates",         v: summary.templates },
    { k: "expert",       label: "Expert requests",   v: summary.expert_requests },
    { k: "files",        label: "Stored files",      v: summary.files },
    { k: "reviews",      label: "Document reviews",  v: summary.reviews },
    { k: "signed",       label: "Signed documents",  v: summary.signed_documents },
  ] : [];
  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-6xl">
      <div className="kicker mb-3">— Admin Portal</div>
      <h1 className="font-serif text-4xl md:text-5xl mb-10">Overview</h1>
      {err && <div className="text-sm text-red-400 mb-6">{err}</div>}
      {!summary && !err && <div className="text-[var(--td-text-3)] font-mono text-xs">Loading…</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <div key={t.k} className="card-surface p-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">{t.label}</div>
            <div className="font-serif text-4xl text-[var(--td-gold)] mt-2">{t.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminStub({ title, subtitle }) {
  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-4xl">
      <div className="kicker mb-3">— Admin Portal</div>
      <h1 className="font-serif text-4xl md:text-5xl mb-6">{title}</h1>
      <div className="card-surface p-8">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)] mb-3">
          Coming next
        </div>
        <p className="text-[var(--td-text-2)] leading-relaxed">
          {subtitle || "This admin surface is being built as part of the next TradeDocs releases (2.2–2.3)."}
        </p>
      </div>
    </div>
  );
}
